import { jwtDecode, JwtPayload } from 'jwt-decode';
import { LoggingSubject, testLog } from '../logging/test-logging';

/*
Decoded access token example, with additional expiration date embedded:
{
  "sessionId": "104673ff-1302-458e-877f-3726734feab1",
  "uid": "ce7a5e00-41b6-4de1-bbb3-20eb888d154d",
  "browserExtensionSession": {
    "browserExtensionSessionId": "68768332-ac62-48c8-bfca-8dcfb033652f",
    "sessionToken": "611f1ec8-30af-4dc7-a46c-405dda4bcc62",
    "prizeoutUserId": "43e064da-084d-42e6-86bd-9216d6506f75",
    "browserExtensionLinkId": "872fb6ed-b5ee-4179-a403-ffaabb7f6b60",
    "expirationDate": "2025-04-12T20:12:07.942Z",
    "createDate": "2025-03-13T20:12:07.942Z",
    "updateDate": "2025-03-13T20:12:07.942Z"
  },
  "iat": 1741896727,
  "exp": 1744488726
}
*/
export interface AccessTokenJwtPayload extends JwtPayload {
    browserExtensionSession?: {
        expirationDate?: string;
    };
}

// Standard JWT format - applies to the session token and user token
export const isJWTTokenExpired = (token: string): boolean => {
    if (!token) {
        return true;
    }

    const decodedToken = jwtDecode<JwtPayload>(token);
    const exp = decodedToken.exp * 1000;
    const now = Date.now();

    testLog(
        LoggingSubject.JWT_STATUS,
        `isJWTokenExpired Token EXP: ${new Date(exp)} (${exp}), Now: ${new Date(now)} (${now})`,
    );

    return exp <= now;
};

// Special case for access tokens, which have an additional expiration date field (see AccessTokenJwtPayload)
export const isJWTAccessTokenExpired = (accessToken: string): boolean => {
    if (!accessToken) {
        return true;
    }

    const decodedToken = jwtDecode<AccessTokenJwtPayload>(accessToken);
    let exp = decodedToken.exp * 1000;
    const now = Date.now();

    const altExpirationDateString = decodedToken.browserExtensionSession?.expirationDate;
    if (altExpirationDateString) {
        const altExp: number = new Date(altExpirationDateString).getTime();
        exp = Math.min(exp, altExp);
    }

    testLog(
        LoggingSubject.JWT_STATUS,
        `isJWTAccessTokenExpired Token EXP: ${new Date(exp)} (${exp}), Now: ${new Date(now)} (${now})`,
    );

    return exp <= now;
};

// This is entirely a diagnostic.  There are places we want to be able to see in the logs
// whether the token is expired, but we don't want to change the operations there in
// response, e.g., API calls.
export const jwtTokenExpirationLogging = (tokenType: string, token: string): void => {
    const tokenIsExpired = isJWTTokenExpired(token);
    testLog(LoggingSubject.JWT_STATUS, `"${tokenType}" is expired? ${tokenIsExpired}`);
    if (tokenIsExpired) {
        const decodedToken = jwtDecode(token);

        testLog(LoggingSubject.JWT_STATUS, `"${tokenType}"`, decodedToken);
    }
};
